// Port of SuiteModel/ManuscriptSelection.swift: which fragments count, decided in one place.
// Counted text: the manuscript arrangement in reading order, folders' own text included, trash left out — or, with
// "count all text", every fragment that isn't trashed (manuscript first, then the rest by id). A map is never counted.
// Scenes: the manuscript's fragments of the kinds asked for (prose by default); a folder counts only with text of its own.
import type { FragmentKind } from '../contract.ts';
import { orderedFragmentIDs, tree, walkTree, type ArrangementTreeNode } from './arrangement.ts';
import type { Arrangement } from './documents.ts';
import { swiftCompare } from './order.ts';

export interface SelectionEntry { kind: FragmentKind; trashed: boolean }

export interface Scene {
  id: string;
  /** A folder (or a document with children): include it only if its own text isn't empty. */
  isFolder: boolean;
}

/** The kinds stored as the prose dialect (everything but a screenplay's Fountain and a map): what Fix Quotes, Clean Up
 *  and the other prose rules may rewrite. */
export const MARKDOWN_KINDS: readonly FragmentKind[] = ['prose', 'note', 'research', 'front-matter'];

const unique = (ids: string[]) => { const seen = new Set<string>(); return ids.filter((id) => !seen.has(id) && (seen.add(id), true)); };

/** `ManuscriptSelection.counted`. */
export function counted(manuscript: Arrangement | null, entries: Map<string, SelectionEntry>, allText: boolean,
  kinds: readonly FragmentKind[] | null = null, built?: ArrangementTreeNode[]): string[] {
  const wanted = (e: SelectionEntry) => !e.trashed && e.kind !== 'map' && (kinds === null || kinds.includes(e.kind));
  const order = (manuscript ? orderedFragmentIDs(manuscript, built ?? tree(manuscript)) : [])
    .filter((id) => { const e = entries.get(id); return e ? wanted(e) : false; });
  if (!allText) return unique(order);
  const inManuscript = new Set(order);
  const rest = [...entries].filter(([id, e]) => wanted(e) && !inManuscript.has(id)).map(([id]) => id).sort(swiftCompare);
  return [...unique(order), ...rest];
}

/** `ManuscriptSelection.scenes`: the scenes of an arrangement in reading order. */
export function scenes(arrangement: Arrangement | null, entries: Map<string, SelectionEntry>,
  kinds: readonly FragmentKind[] = ['prose'], built?: ArrangementTreeNode[]): Scene[] {
  const out: Scene[] = [];
  const seen = new Set<string>();
  for (const { tree: n } of walkTree(built ?? (arrangement ? tree(arrangement) : []))) {
    const id = n.node.fragmentID;
    const entry = id !== undefined ? entries.get(id) : undefined;
    if (id !== undefined && entry && !entry.trashed && kinds.includes(entry.kind) && !seen.has(id)) {
      seen.add(id);
      out.push({ id, isFolder: n.node.isFolder || n.children.length > 0 });
    }
  }
  return out;
}
